import React, { useCallback, useEffect, useRef, useState } from "react";
import { RotateCcw, AlertCircle } from "lucide-react";
import CustomDropdown from "../../../Ui_Components/CustomDropdown";
import { DropdownOption } from "../../../Ui_Components/CustomDropdown";
import DOBPicker from "../components/DOBPicker";
import {
  getNutritionistRole,
  getNutritionistJobTypes,
  getReportingManagerList,
  getPermissionsListForRole,
} from "../../../../services/team";
import { RoleReportingData, RoleReportingErrors, PermissionsState } from "../types";

interface RoleReportingStepProps {
  data: RoleReportingData;
  onChange: (data: RoleReportingData) => void;
  errors?: RoleReportingErrors;
}

const errorRingCls = "ring-1 ring-red-400 rounded-md";

interface ApiAssignRole {
  assign_role_id: number;
  assign_role: string;
}

interface ApiJobType {
  job_type_id: number;
  job_type: string;
}

interface ApiReportingManager {
  nutrist_id: number;
  nutrist_fname: string;
  nutrist_lname: string;
  nutrist_assign_role: string;
}

type ContentOpKey = "create" | "delete" | "edit" | "view";

interface ApiActionPermission {
  resource: string;
  action: string;
  label: string;
  enabled: boolean;
}

interface ApiContentItem {
  resource: string;
  resource_label: string;
  operations: Record<ContentOpKey, { enabled: boolean }>;
}

interface RolePermissionsResponse {
  success: string;
  role: string;
  role_label: string;
  total_count: number;
  enabled_count: number;
  actions: ApiActionPermission[];
  content: ApiContentItem[];
}

const CONTENT_OPS: ContentOpKey[] = ["create", "delete", "edit", "view"];
const CONTENT_OP_LABEL: Record<ContentOpKey, string> = {
  create: "Create",
  delete: "Delete",
  edit: "Edit",
  view: "View",
};

const actionKey = (resource: string, action: string) =>
  `${resource}:${action}`;

const emptyContentPerm = () => ({
  create: false,
  delete: false,
  edit: false,
  view: false,
});

/* Backward-compat export. The previous hardcoded defaults are now sourced from
   GET_PERMISSIONS_LIST_FOR_ROLE; any consumer still importing this gets an
   empty map and falls back to the generic empty permissions shape. */
export const DEFAULT_PERMISSIONS: Record<string, PermissionsState> = {};

/* ── Shimmer ── */
const shimmerStyles = `
  @keyframes shimmer {
    0%   { background-position: -1000px 0; }
    100% { background-position:  1000px 0; }
  }
  .rr-shimmer {
    animation: shimmer 2s infinite linear;
    background: linear-gradient(
      to right,
      #f0f0f0 0%, #e6e6e6 20%, #f0f0f0 40%, #f0f0f0 100%
    );
    background-size: 1000px 100%;
  }
`;

const PermissionsShimmer: React.FC = () => (
  <>
    <style>{shimmerStyles}</style>
    <div className="mb-6">
      <div className="h-3 w-20 rounded rr-shimmer mb-3" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {Array.from({ length: 9 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center justify-between px-3 py-2.5 border border-[#E8E8E8] rounded-md"
          >
            <div className="h-3 w-32 rounded rr-shimmer" />
            <div className="h-4 w-8 rounded-full rr-shimmer" />
          </div>
        ))}
      </div>
    </div>
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="h-3 w-32 rounded rr-shimmer" />
        <div className="h-3 w-48 rounded rr-shimmer" />
      </div>
      <div className="border border-[#F3F4F6] rounded-md overflow-hidden">
        {Array.from({ length: 5 }).map((_, idx, arr) => (
          <div
            key={idx}
            className={`flex items-center justify-between px-4 py-3 ${
              idx < arr.length - 1 ? "border-b border-[#F3F4F6]" : ""
            }`}
          >
            <div className="h-3 w-28 rounded rr-shimmer" />
            <div className="flex items-center gap-6">
              {Array.from({ length: 4 }).map((__, j) => (
                <div key={j} className="flex items-center gap-1.5">
                  <div className="h-4 w-4 rounded rr-shimmer" />
                  <div className="h-3 w-12 rounded rr-shimmer" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </>
);

/* ── Toggle switch ── */
const Toggle: React.FC<{
  checked: boolean;
  onChange: () => void;
  locked?: boolean;
}> = ({ checked, onChange, locked }) => (
  <button
    type="button"
    onClick={locked ? undefined : onChange}
    disabled={locked}
    title={locked ? "Default permission — locked" : undefined}
    className={`relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
      checked ? "bg-[#0DB5B5]" : "bg-[#D1D5DB]"
    } ${locked ? "cursor-not-allowed opacity-20" : ""}`}
  >
    <span
      className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-[18px]" : "translate-x-1"
      }`}
    />
  </button>
);

/* ── Checkbox ── */
const Checkbox: React.FC<{
  checked: boolean;
  onChange: () => void;
  label: string;
  locked?: boolean;
}> = ({ checked, onChange, label, locked }) => (
  <label
    className={`flex items-center gap-1.5 select-none ${
      locked ? "cursor-not-allowed" : "cursor-pointer"
    }`}
  >
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={locked ? undefined : onChange}
      disabled={locked}
      title={locked ? "Default permission — locked" : undefined}
      className={`w-4 h-4 rounded-[2px] flex items-center justify-center flex-shrink-0 border transition-colors ${
        checked ? "bg-[#0DB5B5] border-[#0DB5B5]" : "bg-white border-[#D1D5DB]"
      } ${locked ? "cursor-not-allowed opacity-60" : ""}`}
    >
      {checked && (
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path
            d="M1 4L3.5 6.5L9 1"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
    <span
      className={`text-[12px] 3xl:text-sm font-inter text-[#003E3E] ${
        locked ? "opacity-20" : ""
      }`}
    >
      {label}
    </span>
  </label>
);

/* ── Main component ── */
const RoleReportingStep: React.FC<RoleReportingStepProps> = ({
  data,
  onChange,
  errors = {},
}) => {
  const [roleOptions, setRoleOptions] = useState<DropdownOption[]>([]);
  const [jobTypeOptions, setJobTypeOptions] = useState<DropdownOption[]>([]);
  const [reportingManagerNameOptions, setReportingManagerNameOptions] =
    useState<DropdownOption[]>([]);

  /* Permission state driven by GET_PERMISSIONS_LIST_FOR_ROLE */
  const [actionDefs, setActionDefs] = useState<ApiActionPermission[]>([]);
  const [contentDefs, setContentDefs] = useState<ApiContentItem[]>([]);
  const [defaultPermissions, setDefaultPermissions] =
    useState<PermissionsState | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [activeRoleLabel, setActiveRoleLabel] = useState("");
  const [permsLoading, setPermsLoading] = useState(false);
  const [permsError, setPermsError] = useState<string | null>(null);

  /* Refs let the async loader read the latest data/onChange without forcing
     loadRolePermissions to re-create on every parent update. */
  const dataRef = useRef(data);
  const onChangeRef = useRef(onChange);
  dataRef.current = data;
  onChangeRef.current = onChange;

  /* Ignore stale fetch responses when the user changes role quickly. */
  const requestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const fetchRoles = async () => {
      try {
        const json = await getNutritionistRole();
        if (cancelled || json.success !== "1") return;
        const list: DropdownOption[] = (
          json.nutritionist_assign_role_data ?? []
        ).map((r: ApiAssignRole) => {
          const label = r.assign_role.trim();
          return { value: label, label };
        });
        setRoleOptions(list);
      } catch {
        /* leave empty on failure */
      }
    };

    const fetchJobTypes = async () => {
      try {
        const json = await getNutritionistJobTypes();
        if (cancelled || json.success !== "1") return;
        const list: DropdownOption[] = (
          json.nutritionist_job_type_data ?? []
        ).map((j: ApiJobType) => {
          const label = j.job_type.trim();
          return { value: label, label };
        });
        setJobTypeOptions(list);
      } catch {
        /* leave empty on failure */
      }
    };

    const fetchReportingManagers = async () => {
      try {
        const json = await getReportingManagerList();
        if (cancelled || json.success !== "1") return;
        const list: DropdownOption[] = (json.reporting_manager_data ?? []).map(
          (m: ApiReportingManager) => {
            const name = `${m.nutrist_fname ?? ""} ${
              m.nutrist_lname ?? ""
            }`.trim();
            const role = m.nutrist_assign_role?.trim();
            return {
              value: name,
              label: role ? `${name} (${role})` : name,
            };
          },
        );
        setReportingManagerNameOptions(list);
      } catch {
        /* leave empty on failure */
      }
    };

    fetchRoles();
    fetchJobTypes();
    fetchReportingManagers();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (patch: Partial<RoleReportingData>) =>
    onChange({ ...data, ...patch });

  const buildPermissionsFromApi = (
    apiData: RolePermissionsResponse,
  ): PermissionsState => {
    const actions: Record<string, boolean> = {};
    (apiData.actions ?? []).forEach((a) => {
      actions[actionKey(a.resource, a.action)] = !!a.enabled;
    });
    const content: PermissionsState["content"] = {};
    (apiData.content ?? []).forEach((c) => {
      content[c.resource] = {
        create: !!c.operations?.create?.enabled,
        delete: !!c.operations?.delete?.enabled,
        edit: !!c.operations?.edit?.enabled,
        view: !!c.operations?.view?.enabled,
      };
    });
    return { actions, content };
  };

  const loadRolePermissions = useCallback(async (role: string) => {
    if (!role) return;
    const reqId = ++requestIdRef.current;
    setPermsLoading(true);
    setPermsError(null);
    try {
      const json: RolePermissionsResponse = await getPermissionsListForRole(role);
      if (json.success !== "1")
        throw new Error("Failed to load role permissions");
      if (reqId !== requestIdRef.current) return;

      const built = buildPermissionsFromApi(json);
      setActionDefs(json.actions ?? []);
      setContentDefs(json.content ?? []);
      setTotalCount(json.total_count ?? 0);
      setActiveRoleLabel(json.role_label || role);
      setDefaultPermissions(built);
      onChangeRef.current({ ...dataRef.current, permissions: built });
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setPermsError(
        err instanceof Error ? err.message : "Failed to load role permissions",
      );
      setActionDefs([]);
      setContentDefs([]);
      setTotalCount(0);
      setDefaultPermissions(null);
    } finally {
      if (reqId === requestIdRef.current) setPermsLoading(false);
    }
  }, []);

  /* If the form already has a role (edit mode), fetch its permissions on mount. */
  useEffect(() => {
    if (data.role) loadRolePermissions(data.role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRoleChange = (role: string) => {
    update({ role, permissions: { actions: {}, content: {} } });
    loadRolePermissions(role);
  };

  /* ── Permissions helpers ── */
  const perm = data.permissions;

  const countEnabled = (): number => {
    const actionCount = actionDefs.filter(
      (a) => perm.actions[actionKey(a.resource, a.action)],
    ).length;
    const contentCount = contentDefs.reduce((sum, item) => {
      const c = perm.content[item.resource] ?? emptyContentPerm();
      return sum + CONTENT_OPS.reduce((s, op) => s + (c[op] ? 1 : 0), 0);
    }, 0);
    return actionCount + contentCount;
  };

  const countRemovedFromDefault = (): number => {
    if (!defaultPermissions) return 0;
    let removed = 0;
    actionDefs.forEach((a) => {
      const k = actionKey(a.resource, a.action);
      if (defaultPermissions.actions[k] && !perm.actions[k]) removed++;
    });
    contentDefs.forEach((item) => {
      const def =
        defaultPermissions.content[item.resource] ?? emptyContentPerm();
      const cur = perm.content[item.resource] ?? emptyContentPerm();
      CONTENT_OPS.forEach((op) => {
        if (def[op] && !cur[op]) removed++;
      });
    });
    return removed;
  };

  const isModified = (): boolean => {
    if (!defaultPermissions) return false;
    const actionDiff = actionDefs.some((a) => {
      const k = actionKey(a.resource, a.action);
      return (
        (defaultPermissions.actions[k] ?? false) !== (perm.actions[k] ?? false)
      );
    });
    const contentDiff = contentDefs.some((item) => {
      const def =
        defaultPermissions.content[item.resource] ?? emptyContentPerm();
      const cur = perm.content[item.resource] ?? emptyContentPerm();
      return CONTENT_OPS.some((op) => def[op] !== cur[op]);
    });
    return actionDiff || contentDiff;
  };

  const resetToDefault = () => {
    if (!defaultPermissions) return;
    update({ permissions: defaultPermissions });
  };

  const isActionLocked = (resource: string, action: string) =>
    !!defaultPermissions?.actions[actionKey(resource, action)];

  const isContentOpLocked = (resource: string, op: ContentOpKey) =>
    !!defaultPermissions?.content[resource]?.[op];

  const toggleAction = (resource: string, action: string) => {
    if (isActionLocked(resource, action)) return;
    const k = actionKey(resource, action);
    update({
      permissions: {
        ...perm,
        actions: { ...perm.actions, [k]: !perm.actions[k] },
      },
    });
  };

  const toggleContent = (resource: string, op: ContentOpKey) => {
    if (isContentOpLocked(resource, op)) return;
    const current = perm.content[resource] ?? emptyContentPerm();
    update({
      permissions: {
        ...perm,
        content: {
          ...perm.content,
          [resource]: { ...current, [op]: !current[op] },
        },
      },
    });
  };

  const enabledCount = data.role && !permsLoading ? countEnabled() : 0;
  const removedCount = countRemovedFromDefault();
  const modified = isModified();
  const displayRoleLabel = activeRoleLabel || data.role;

  return (
    <div className="space-y-4">
      {/* ── Role & Job card ── */}
      <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
              Role
            </label>
            <CustomDropdown
              options={roleOptions}
              value={data.role}
              onChange={handleRoleChange}
              placeholder="Select role"
              className={errors.role ? errorRingCls : ""}
            />
            {errors.role && (
              <p className="text-xs 3xl:text-sm text-red-500 font-inter">
                {errors.role}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
              Job type
            </label>
            <CustomDropdown
              options={jobTypeOptions}
              value={data.jobType}
              onChange={(v) => update({ jobType: v })}
              placeholder="Select job type"
              className={errors.jobType ? errorRingCls : ""}
            />
            {errors.jobType && (
              <p className="text-xs 3xl:text-sm text-red-500 font-inter">
                {errors.jobType}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
              Joining date
            </label>
            <DOBPicker
              value={data.joiningDate}
              onChange={(v) => update({ joiningDate: v })}
              error={errors.joiningDate}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
              Reporting manager name
            </label>
            <CustomDropdown
              options={reportingManagerNameOptions}
              value={data.reportingManagerName}
              onChange={(v) => update({ reportingManagerName: v })}
              placeholder="eg. Dipanwita Biswas"
              className={errors.reportingManagerName ? errorRingCls : ""}
            />
            {errors.reportingManagerName && (
              <p className="text-xs 3xl:text-sm text-red-500 font-inter">
                {errors.reportingManagerName}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Access & Permissions card ── */}
      <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F]">
              Access &amp; permissions
            </h3>
            <p className="text-xs 3xl:text-sm font-inter text-[#646464] mt-0.5">
              These are granted automatically based on the selected role.
            </p>
          </div>

          {data.role && !permsLoading && !permsError && (
            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
              <span className="px-2.5 py-1 rounded-full bg-[#F3F4F6] text-xs 3xl:text-sm font-medium font-inter text-[#1F1F1F]">
                {displayRoleLabel}
              </span>
              <span className="text-xs 3xl:text-sm font-inter text-[#646464]">
                {enabledCount} of {totalCount} enabled
              </span>
              {modified && (
                <button
                  type="button"
                  onClick={resetToDefault}
                  className="flex items-center gap-1 text-xs 3xl:text-sm font-medium font-inter text-[#0DB5B5] hover:text-[#00A9A9] transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset to default
                </button>
              )}
            </div>
          )}
        </div>

        {modified && removedCount > 0 && (
          <div className="flex items-center gap-2 bg-[#FFF8EC] border border-[#FFE4A0] rounded-md px-3 py-2 mb-4">
            <AlertCircle className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
            <p className="text-xs 3xl:text-sm font-inter text-[#92400E]">
              {removedCount} permission{removedCount > 1 ? "s" : ""} removed
              from the {displayRoleLabel} default, this person will have custom
              access.
            </p>
          </div>
        )}

        {permsError && (
          <p className="mb-4 text-sm font-inter text-red-500">{permsError}</p>
        )}

        {!data.role ? (
          <div className="border border-[#E0E0E0] bg-[#F9F9F9] rounded-md p-3 flex items-center justify-center">
            <p className="text-sm 3xl:text-base font-inter text-[#9CA3AF] text-center">
              Select a role above to preview the access this nutritionist will
              receive.
            </p>
          </div>
        ) : permsLoading ? (
          <PermissionsShimmer />
        ) : (
          <>
            {/* ── Actions ── */}
            <div className="mb-6">
              <p className="text-sm 3xl:text-base font-inter text-[#1F1F1F] tracking-wide mb-3">
                Actions
              </p>
              {actionDefs.length === 0 ? (
                <p className="text-sm font-inter text-gray-400">
                  No actions for this role
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {actionDefs.map((action) => {
                    const k = actionKey(action.resource, action.action);
                    const locked = isActionLocked(
                      action.resource,
                      action.action,
                    );
                    return (
                      <div
                        key={k}
                        className="flex items-center justify-between px-3 py-2.5 border border-[#E8E8E8] rounded-md"
                      >
                        <span
                          className={`text-[12px] 3xl:text-sm font-inter text-[#003E3E] ${
                            locked ? "opacity-50" : ""
                          }`}
                        >
                          {action.label}
                        </span>
                        <Toggle
                          checked={!!perm.actions[k]}
                          locked={locked}
                          onChange={() =>
                            toggleAction(action.resource, action.action)
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Manage content ── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm 3xl:text-base font-inter text-[#1F1F1F]  tracking-wide">
                  Manage content
                </p>
                <span className="text-xs 3xl:text-sm font-inter text-[#828282]">
                  Toggle create / delete / edit / view
                </span>
              </div>

              {contentDefs.length === 0 ? (
                <p className="text-sm font-inter text-gray-400">
                  No content permissions for this role
                </p>
              ) : (
                <div className="border border-[#F3F4F6] rounded-md overflow-hidden">
                  {contentDefs.map((item, idx) => {
                    const c =
                      perm.content[item.resource] ?? emptyContentPerm();
                    return (
                      <div
                        key={item.resource}
                        className={`flex items-center justify-between px-4 py-3 ${
                          idx < contentDefs.length - 1
                            ? "border-b border-[#F3F4F6]"
                            : ""
                        }`}
                      >
                        <span className="text-[12px] 3xl:text-sm font-inter text-[#003E3E]">
                          {item.resource_label}
                        </span>
                        <div className="flex items-center gap-6">
                          {CONTENT_OPS.map((op) => (
                            <Checkbox
                              key={op}
                              checked={c[op]}
                              locked={isContentOpLocked(item.resource, op)}
                              onChange={() => toggleContent(item.resource, op)}
                              label={CONTENT_OP_LABEL[op]}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RoleReportingStep;
